import React, { useMemo, useContext, useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import DataTable from "examples/Tables/DataTable";
import { AuthContext } from "../../../../../context/index";
import { IconButton, Tooltip, Modal, Box } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import {
    MapContainer,
    TileLayer,
    Marker,
    Popup,
    Polyline,
    useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-polylinedecorator";
import MDTypography from "../../../../../components/MDTypography";
import { appConfirm } from "components/AppDialog";
import { RemoveRowButton } from "components/RowActions";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const startIcon = L.icon({
    iconUrl: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
});

const endIcon = L.icon({
    iconUrl: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
});

const transitIcon = L.icon({
    iconUrl: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
});

const createBadgeIcon = (label, variant = "default") => {
    const styles = {
        start: {
            bg: "#16a34a",
            color: "#ffffff",
            border: "#15803d",
            shadow: "0 6px 18px rgba(22,163,74,0.28)",
        },
        end: {
            bg: "#dc2626",
            color: "#ffffff",
            border: "#b91c1c",
            shadow: "0 6px 18px rgba(220,38,38,0.28)",
        },
        point: {
            bg: "#2563eb",
            color: "#ffffff",
            border: "#1d4ed8",
            shadow: "0 6px 18px rgba(37,99,235,0.28)",
        },
        default: {
            bg: "#334155",
            color: "#ffffff",
            border: "#1e293b",
            shadow: "0 6px 18px rgba(15,23,42,0.22)",
        },
    };

    const s = styles[variant] || styles.default;

    return L.divIcon({
        className: "custom-route-badge-icon",
        html: `
      <div
        style="
          display:inline-flex;
          align-items:center;
          justify-content:center;
          min-width:34px;
          height:28px;
          padding:0 10px;
          border-radius:999px;
          background:${s.bg};
          color:${s.color};
          border:1px solid ${s.border};
          font-size:12px;
          font-weight:700;
          letter-spacing:0.3px;
          box-shadow:${s.shadow};
          font-family:Inter, Arial, sans-serif;
          white-space:nowrap;
        "
      >
        ${label}
      </div>
    `,
        iconSize: [72, 28],
        iconAnchor: [36, 14],
    });
};

function ArrowsBetweenLocations({ positions }) {
    const map = useMap();

    useEffect(() => {
        if (!positions || positions.length < 2) return undefined;

        const decorator = L.polylineDecorator(L.polyline(positions), {
            patterns: [
                {
                    offset: "100%",
                    repeat: 0,
                    symbol: L.Symbol.arrowHead({
                        pixelSize: 12,
                        polygon: false,
                        pathOptions: { stroke: true, color: "blue" },
                    }),
                },
            ],
        });

        decorator.addTo(map);

        return () => {
            map.removeLayer(decorator);
        };
    }, [map, positions]);

    return null;
}

function FitBounds({ positions }) {
    const map = useMap();

    useEffect(() => {
        if (!positions || !positions.length) return;
        const bounds = L.latLngBounds(positions);
        map.fitBounds(bounds, { padding: [40, 40] });
    }, [map, positions]);

    return null;
}

function RoutePreviewModal({ open, onClose, title, points }) {
    const validPoints = (Array.isArray(points) ? points : []).filter(
        (p) => Number.isFinite(Number(p?.lat)) && Number.isFinite(Number(p?.lon))
    );

    const polylinePositions = validPoints.map((p) => [Number(p.lat), Number(p.lon)]);
    const deliveryCount = validPoints.filter((p) => p.type === "delivery").length;

    return (
        <Modal
            open={open}
            onClose={onClose}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: "rgba(0, 0, 0, 0.65)",
                        backdropFilter: "blur(2px)",
                    },
                },
            }}
        >
            <Box
                sx={{
                    bgcolor: "white",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "85%",
                    height: "85%",
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: 24,
                }}
            >
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        display: "grid",
                        gridTemplateColumns: "1fr auto 1fr",
                        alignItems: "center",
                        borderBottom: "1px solid rgba(255,255,255,0.12)",
                        background: "rgba(15, 23, 42, 0.92)",
                        color: "#fff",
                    }}
                >
                    <Box />

                    <Box
                        sx={{
                            px: 1.5,
                            py: 0.75,
                            borderRadius: "10px",
                            backgroundColor: "rgba(255,255,255,0.08)",
                            backdropFilter: "blur(4px)",
                            justifySelf: "center",
                        }}
                    >
                        <strong style={{ color: "#fff", fontSize: "18px" }}>{title}</strong>
                    </Box>

                    <Box sx={{ justifySelf: "end" }}>
                        <IconButton
                            onClick={onClose}
                            sx={{
                                color: "#fff",
                                backgroundColor: "rgba(255,255,255,0.08)",
                                "&:hover": {
                                    backgroundColor: "rgba(255,255,255,0.16)",
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>

                <Box sx={{ height: "calc(100% - 60px)" }}>
                    <MapContainer center={[39.92, 32.85]} zoom={5} style={{ height: "100%", width: "100%" }}>
                        <TileLayer
                            attribution="&copy; OpenStreetMap contributors"
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />

                        {polylinePositions.length > 0 && <FitBounds positions={polylinePositions} />}

                        {validPoints.map((point, index) => {
                            const isPickup = point.type === "pickup";
                            const isHistory = point.type === "history";
                            const isDelivery = point.type === "delivery";

                            let markerIcon = transitIcon;
                            let markerLabel = "";
                            let badgeVariant = "point";

                            if (isPickup) {
                                markerIcon = startIcon;
                                markerLabel = "START";
                                badgeVariant = "start";
                            } else if (isDelivery) {
                                markerIcon = endIcon;
                                badgeVariant = "end";

                                if (deliveryCount === 1) {
                                    markerLabel = "DELIVERY";
                                } else {
                                    const deliveryIndex = validPoints
                                        .slice(0, index + 1)
                                        .filter((p) => p.type === "delivery").length;

                                    markerLabel = `DELIVERY ${deliveryIndex}`;
                                }
                            } else if (isHistory) {
                                markerIcon = transitIcon;
                                badgeVariant = "point";

                                const historyIndex = validPoints
                                    .slice(0, index + 1)
                                    .filter((p) => p.type === "history").length;

                                markerLabel = `P${historyIndex}`;
                            } else {
                                markerLabel = `${index + 1}`;
                            }

                            return (
                                <React.Fragment key={`${point.type || "p"}-${index}`}>
                                    <Marker
                                        position={[Number(point.lat), Number(point.lon)]}
                                        icon={markerIcon}
                                    >
                                        <Popup>
                                            <strong>{point.labelType || point.type || `Point ${index + 1}`}</strong>
                                            <br />
                                            {point.label || "-"}
                                        </Popup>
                                    </Marker>

                                    <Marker
                                        position={[Number(point.lat), Number(point.lon)]}
                                        icon={createBadgeIcon(markerLabel, badgeVariant)}
                                        interactive={false}
                                        zIndexOffset={1000}
                                    />
                                </React.Fragment>
                            );
                        })}

                        {polylinePositions.length > 1 && (
                            <>
                                <Polyline
                                    positions={polylinePositions}
                                    pathOptions={{
                                        color: "#2962ff",
                                        weight: 5,
                                        opacity: 0.85,
                                        dashArray: "8, 12",
                                        lineCap: "round",
                                        lineJoin: "round",
                                    }}
                                />
                                <ArrowsBetweenLocations positions={polylinePositions} />
                            </>
                        )}
                    </MapContainer>
                </Box>
            </Box>
        </Modal>
    );
}

function LocationHistory({
                             omblNumber,
                             omblId,
                             onGoOmbl,
                             onGoShipment,
                             selectedShipmentsState,
                             locationHistory,
                             setLocationHistory,
                             pickupDate,
                             pickupTime,
                             pickupLocation,
                             proofOfDelivery,
                             proofOfDeliveryTime,
                             deliveryLocation,
                             service,
                             pickupAddress1,
                             pickupAddress2,
                             deliveryAddress1,
                             deliveryAddress2,
                         }) {
    const { user } = useContext(AuthContext);
    const [currentUserName, setCurrentUserName] = useState("-");
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewTitle, setPreviewTitle] = useState("");
    const [previewPoints, setPreviewPoints] = useState([]);
    const [serviceAddressPoints, setServiceAddressPoints] = useState([]);

    useEffect(() => {
        setCurrentUserName(user?.name || "Unknown User");
    }, [user?.name]);

    const linkTextSx = {
        cursor: "pointer",
        textDecoration: "underline",
        "&:hover": {
            opacity: 0.8,
        },
    };

    const isDeliveryRecord = (loc) => {
        const typeValue = String(loc?.type || "").toLowerCase().trim();
        const eventTypeValue = String(loc?.eventType || "").toLowerCase().trim();

        return (
            typeValue === "delivery" ||
            eventTypeValue.includes("delivery address") ||
            eventTypeValue.includes("pod") ||
            eventTypeValue.includes("fpod")
        );
    };

    const connectedAhfls = useMemo(() => {
        return (Array.isArray(selectedShipmentsState) ? selectedShipmentsState : [])
            .map((shipment) => {
                const shipmentId = shipment?.id || shipment?._id;
                const attr = shipment?.attributes || shipment || {};
                const shipmentNo = attr?.shipmentNo;
                const airMode = String(attr?.airTransportType || "").toUpperCase();
                const roadMode = String(attr?.roadTransportType || "").toUpperCase();

                if (!shipmentId || !shipmentNo) return null;

                const prefix =
                    airMode.includes("FCL") || airMode.includes("LCL")
                        ? "AHFL"
                        : "RHFL";

                return {
                    id: shipmentId,
                    label: `${prefix}${shipmentNo}`,
                };
            })
            .filter(Boolean);
    }, [selectedShipmentsState]);


    const renderOmblLink = () => {
        if (!omblNumber) return "-";
        if (!omblId || !onGoOmbl) return omblNumber;

        return (
            <MDTypography
                variant="caption"
                fontWeight="bold"
                color="info"
                sx={linkTextSx}
                onClick={onGoOmbl}
            >
                {omblNumber}
            </MDTypography>
        );
    };

    const renderConnectedAhfls = () => {
        if (!connectedAhfls.length) return "-";

        return (
            <MDBox display="flex" flexWrap="wrap" gap={0.75}>
                {connectedAhfls.map((item) => (
                    <MDTypography
                        key={item.id}
                        variant="caption"
                        fontWeight="bold"
                        color="info"
                        sx={linkTextSx}
                        onClick={() => onGoShipment && onGoShipment(item.id)}
                    >
                        {item.label}
                    </MDTypography>
                ))}
            </MDBox>
        );
    };

    const formatDateTime = (datetimeString) => {
        if (!datetimeString) return "-";

        const dt = new Date(datetimeString);
        if (Number.isNaN(dt.getTime())) return "-";

        const date = dt.toLocaleDateString("tr-TR");
        const time = dt.toLocaleTimeString("tr-TR", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });

        return `${date} ${time}`;
    };

    const combineDateTime = (date, time) => {
        if (!date) return "-";

        const baseDate = new Date(date);
        if (Number.isNaN(baseDate.getTime())) return "-";

        const yyyy = baseDate.getFullYear();
        const mm = String(baseDate.getMonth() + 1).padStart(2, "0");
        const dd = String(baseDate.getDate()).padStart(2, "0");
        const hhmm = time || "00:00";

        return formatDateTime(`${yyyy}-${mm}-${dd}T${hhmm}:00`);
    };

    const getServiceAddressLabel = (addr) => {
        if (!addr) return "-";

        if (addr.type === "port") {
            return [addr.port, addr.country].filter(Boolean).join(", ");
        }

        return [addr.city, addr.country].filter(Boolean).join(", ");
    };

    const getServiceAddressEventName = (key, addr) => {
        const isPort = addr?.type === "port";

        if (key === "pickupAddress1") return isPort ? "POL" : "Pickup Address";
        if (key === "pickupAddress2") return isPort ? "POL 2" : "Pickup Address 2";
        if (key === "deliveryAddress1") return isPort ? "POD" : "FPOD";
        if (key === "deliveryAddress2") return isPort ? "POD 2" : "Delivery Address 2";

        return "Service Address";
    };

    const geocodeServiceAddress = async (addr) => {
        const query =
            addr?.type === "port"
                ? [addr.port, addr.country].filter(Boolean).join(", ")
                : [addr.city, addr.country].filter(Boolean).join(", ");

        if (!query) return null;

        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`
            );
            const data = await res.json();

            if (Array.isArray(data) && data.length > 0) {
                return {
                    lat: Number(data[0].lat),
                    lon: Number(data[0].lon),
                    label: query,
                };
            }
        } catch (err) {
            console.error("service address geocode error:", err);
        }

        return null;
    };

    const formatAddress = (locationObj) => {
        if (!locationObj) return "-";

        const { city, district, zipcode, country, latLong, port } = locationObj;
        const addr = [city, district, zipcode, country, port].filter(Boolean).join(" , ");

        if (addr && addr.trim().length > 0) return addr;
        if (latLong && String(latLong).trim().length > 0) return latLong;

        return "-";
    };

    const parseLatLong = (latLong) => {
        if (!latLong || typeof latLong !== "string") return null;

        const [latStr, lonStr] = latLong.split(",");
        const lat = Number(String(latStr || "").trim());
        const lon = Number(String(lonStr || "").trim());

        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

        return { lat, lon };
    };

    const pickupPoint = useMemo(() => {
        const coords = parseLatLong(pickupLocation?.latLong);
        if (!coords) return null;

        return {
            type: "pickup",
            labelType: "Pickup Address",
            eventType: "Pickup Address",
            label: formatAddress(pickupLocation),
            lat: coords.lat,
            lon: coords.lon,
            dateText: combineDateTime(pickupDate, pickupTime),
        };
    }, [pickupLocation, pickupDate, pickupTime]);

    const pureHistoryPoints = useMemo(() => {
        return (Array.isArray(locationHistory) ? locationHistory : [])
            .map((loc, originalIndex) => ({ loc, originalIndex }))
            .filter(
                ({ loc }) =>
                    !isDeliveryRecord(loc) &&
                    Number.isFinite(Number(loc?.lat)) &&
                    Number.isFinite(Number(loc?.lon))
            )
            .map(({ loc, originalIndex }, index) => ({
                type: "history",
                labelType: `Point ${index + 1}`,
                eventType: `Point ${index + 1}`,
                label: loc?.label || `${loc?.lat}, ${loc?.lon}`,
                lat: Number(loc.lat),
                lon: Number(loc.lon),
                dateText: formatDateTime(loc?.addedAt),
                // handleDeletePoint filters against the ORIGINAL locationHistory array, so this
                // must be the index in that array, not the position after filtering.
                rawIndex: originalIndex,
            }));
    }, [locationHistory]);

    const deliveryPoints = useMemo(() => {
        const baseDeliveryPoints = [];

        const mainDeliveryCoords = parseLatLong(deliveryLocation?.latLong);
        if (mainDeliveryCoords) {
            baseDeliveryPoints.push({
                source: "main",
                type: "delivery",
                label: formatAddress(deliveryLocation),
                lat: mainDeliveryCoords.lat,
                lon: mainDeliveryCoords.lon,
                dateText: combineDateTime(proofOfDelivery, proofOfDeliveryTime),
            });
        }

        const extraDeliveryPoints = (Array.isArray(locationHistory) ? locationHistory : [])
            .filter(
                (loc) =>
                    isDeliveryRecord(loc) &&
                    Number.isFinite(Number(loc?.lat)) &&
                    Number.isFinite(Number(loc?.lon))
            )
            .map((loc) => ({
                source: "history",
                type: "delivery",
                label: loc?.label || `${loc?.lat}, ${loc?.lon}`,
                lat: Number(loc.lat),
                lon: Number(loc.lon),
                dateText: formatDateTime(loc?.addedAt),
            }));

        const merged = [...baseDeliveryPoints, ...extraDeliveryPoints];

        return merged.map((point, index) => {
            const eventLabel =
                merged.length === 1
                    ? "Delivery Address"
                    : `Delivery Address ${index + 1}`;

            return {
                ...point,
                type: "delivery",
                labelType: eventLabel,
                eventType: eventLabel,
                rawIndex: index,
            };
        });
    }, [deliveryLocation, proofOfDelivery, proofOfDeliveryTime, locationHistory]);

    const serviceAddressRows = useMemo(() => {
        return [
            { key: "pickupAddress1", value: pickupAddress1 },
            { key: "pickupAddress2", value: pickupAddress2 },
            { key: "deliveryAddress1", value: deliveryAddress1 },
            { key: "deliveryAddress2", value: deliveryAddress2 },
        ]
            .filter((x) => x.value && (x.value.city || x.value.port || x.value.country))
            .map((x, index) => ({
                amblNo: renderOmblLink(),
                connectedAhfls: renderConnectedAhfls(),
                eventType: getServiceAddressEventName(x.key, x.value),
                updatedBy: currentUserName,
                lastUpdated: "-",
                newLocation: getServiceAddressLabel(x.value),
                actions: (
                    <Tooltip title="Show route">
                        <IconButton onClick={() => openPreviewForRow("service")}>
                            <SearchIcon />
                        </IconButton>
                    </Tooltip>
                ),
                serviceAddress: x.value,
                orderIndex: index,
            }));
    }, [
        pickupAddress1,
        pickupAddress2,
        deliveryAddress1,
        deliveryAddress2,
        currentUserName,
        omblNumber,
        omblId,
        onGoOmbl,
        onGoShipment,
        connectedAhfls,
    ]);

    useEffect(() => {
        let isMounted = true;

        const run = async () => {
            const raw = [
                { key: "pickupAddress1", value: pickupAddress1 },
                { key: "pickupAddress2", value: pickupAddress2 },
                { key: "deliveryAddress1", value: deliveryAddress1 },
                { key: "deliveryAddress2", value: deliveryAddress2 },
            ].filter((x) => x.value && (x.value.city || x.value.port || x.value.country));

            const resolved = await Promise.all(
                raw.map(async ({ key, value }) => {
                    const geo = await geocodeServiceAddress(value);

                    return {
                        key,
                        type: key.startsWith("pickup") ? "pickup" : "delivery",
                        eventType: getServiceAddressEventName(key, value),
                        labelType: getServiceAddressEventName(key, value),
                        label: getServiceAddressLabel(value),
                        lat: geo?.lat,
                        lon: geo?.lon,
                    };
                })
            );

            const next = resolved.filter((x) => Number.isFinite(x.lat) && Number.isFinite(x.lon));

            setServiceAddressPoints((prev) => {
                if (JSON.stringify(prev) === JSON.stringify(next)) {
                    return prev; // 🔥 loop kırıldı
                }
                return next;
            });
        };

        run();

        return () => {
            isMounted = false;
        };
    }, [pickupAddress1, pickupAddress2, deliveryAddress1, deliveryAddress2]);
    const openPreviewForRow = (rowType, rowIndex = null) => {
        let points = [];

        if (rowType === "pickup") {
            points = [
                ...(pickupPoint ? [pickupPoint] : []),
                ...serviceAddressPoints.filter((p) => p.key?.startsWith("pickup")),
            ];
            setPreviewTitle(`Pickup Route Preview - ${omblNumber || "-"}`);
        }

        if (rowType === "service") {
            points = [
                ...(pickupPoint ? [pickupPoint] : []),
                ...serviceAddressPoints,
            ];
            setPreviewTitle(`Service Route Preview - ${omblNumber || "-"}`);
        }

        if (rowType === "history") {
            points = [
                ...(pickupPoint ? [pickupPoint] : []),
                ...serviceAddressPoints,
                ...pureHistoryPoints.slice(0, rowIndex + 1),
            ];
            setPreviewTitle(`Route Preview Until Point ${rowIndex + 1} - ${omblNumber || "-"}`);
        }

        if (rowType === "delivery") {
            points = [
                ...(pickupPoint ? [pickupPoint] : []),
                ...serviceAddressPoints,
                ...pureHistoryPoints,
                ...deliveryPoints,
            ];
            setPreviewTitle(`Full Route Preview - ${omblNumber || "-"}`);
        }

        setPreviewPoints(points);
        setPreviewOpen(true);
    };

    const handleDeletePoint = async (pointIndex) => {
        if (!await appConfirm("Are you sure you want to delete this waypoint?")) return;

        setLocationHistory((prev) =>
            (Array.isArray(prev) ? prev : []).filter((_, index) => index !== pointIndex)
        );
    };

    const rows = useMemo(() => {
        const data = [];

        if (pickupDate || pickupLocation) {
            data.push({
                amblNo: renderOmblLink(),
                connectedAhfls: renderConnectedAhfls(),
                eventType: "Pickup Address",
                updatedBy: currentUserName,
                lastUpdated: combineDateTime(pickupDate, pickupTime),
                newLocation: formatAddress(pickupLocation),
                actions: (
                    <Tooltip title="Show route">
                        <IconButton onClick={() => openPreviewForRow("pickup")}>
                            <SearchIcon />
                        </IconButton>
                    </Tooltip>
                ),
            });
        }

        data.push(...serviceAddressRows);

        pureHistoryPoints.forEach((loc, index) => {
            data.push({
                amblNo: renderOmblLink(),
                connectedAhfls: renderConnectedAhfls(),
                eventType: loc.eventType || `Point ${index + 1}`,
                updatedBy: currentUserName,
                lastUpdated: loc.dateText,
                newLocation: loc.label,
                actions: (
                    <MDBox display="flex" alignItems="center">
                        <Tooltip title="Show route">
                            <IconButton onClick={() => openPreviewForRow("history", index)}>
                                <SearchIcon />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete point">
                            <RemoveRowButton onClick={() => handleDeletePoint(loc.rawIndex)} tooltip="Remove waypoint" />
                        </Tooltip>
                    </MDBox>
                ),
            });
        });

        deliveryPoints.forEach((point) => {
            data.push({
                amblNo: renderOmblLink(),
                connectedAhfls: renderConnectedAhfls(),
                eventType: point.eventType,
                updatedBy: currentUserName,
                lastUpdated: point.dateText,
                newLocation: point.label,
                actions: (
                    <Tooltip title="Show full route">
                        <IconButton onClick={() => openPreviewForRow("delivery")}>
                            <SearchIcon />
                        </IconButton>
                    </Tooltip>
                ),
            });
        });

        return data.filter(
            (row) =>
                row &&
                (
                    row.lastUpdated !== "-" ||
                    (row.newLocation && row.newLocation !== "-")
                )
        );
    }, [
        omblNumber,
        omblId,
        onGoOmbl,
        onGoShipment,
        connectedAhfls,
        currentUserName,
        pickupDate,
        pickupTime,
        pickupLocation,
        pureHistoryPoints,
        deliveryPoints,
        serviceAddressRows,
        serviceAddressPoints,
    ]);

    const columns = [
        {
            Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>AMBL No</span>,
            accessor: "amblNo",
        },
        {
            Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Connected AHFLs</span>,
            accessor: "connectedAhfls",
        },
        {
            Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Event</span>,
            accessor: "eventType",
            disableSortBy: true,
        },
        {
            Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Updated By</span>,
            accessor: "updatedBy",
        },
        {
            Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Last Updated</span>,
            accessor: "lastUpdated",
        },
        {
            Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Current Location</span>,
            accessor: "newLocation",
        },
        {
            Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Actions</span>,
            accessor: "actions",
            disableSortBy: true,
        },
    ];

    return (
        <>
            <Grid container spacing={0} justifyContent="center" mt={1} mb={4}>
                <Grid item xs={12} lg={12} style={{ paddingLeft: 0, paddingRight: 0 }}>
                    <MDBox
                        pt={2}
                        pb={2}
                        px={0}
                        mx="auto"
                        width="100%"
                        maxWidth="unset"
                        style={{ paddingLeft: 0, paddingRight: 0 }}
                    >
                        <DataTable
                            table={{ columns, rows }}
                            canSearch={false}
                            style={{ width: "100%" }}
                        />
                    </MDBox>
                </Grid>
            </Grid>

            <RoutePreviewModal
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                title={previewTitle}
                points={previewPoints}
            />
        </>
    );
}

export default LocationHistory;